<script lang="ts">import { getCasinoLobbyBanners, getHomePageBanners } from "./data";
import { stateUi } from "@infinitro/config";
import { getSubject, locale, toObservable } from "@infinitro/global";
import { delay, map, shareReplay, switchMap, tap } from "rxjs";
import { onMount } from "svelte";
import Swiper from "swiper";
import { Autoplay, EffectCoverflow, FreeMode, Mousewheel, Navigation, Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import Banner from "./parts/banner.svelte";
let { showRightSideOfImage = false, type = "common" } = $props();
const isReady$ = getSubject();
const swiperDestory$ = getSubject();
let swiperContainer = $state();
let startX = 0;
let startY = 0;
// Prevent browser back gesture by detecting horizontal swipes
const handleTouchStart = (e) => {
	startX = e.touches[0].clientX;
	startY = e.touches[0].clientY;
};
const handleTouchMove = (e) => {
	const deltaX = e.touches[0].clientX - startX;
	const deltaY = e.touches[0].clientY - startY;
	// If horizontal swipe detected, prevent default to block browser back
	if (Math.abs(deltaX) > Math.abs(deltaY)) {
		e.preventDefault();
	}
};
const data$ = isReady$.pipe(switchMap(() => toObservable(locale)), map((lang) => {
	const banners = type === "common" ? getCasinoLobbyBanners(lang) : getHomePageBanners(lang);
	return banners;
}), shareReplay(1));
const swiper$ = isReady$.pipe(map(() => {
	Swiper.use([
		Navigation,
		Pagination,
		Autoplay,
		EffectCoverflow,
		FreeMode
	]);
	const ss = new Swiper(swiperContainer, {
		modules: [
			Navigation,
			Autoplay,
			Mousewheel,
			FreeMode
		],
		loop: true,
		pagination: {
			el: ".sp",
			clickable: true,
			type: "bullets"
		},
		mousewheel: { forceToAxis: true },
		navigation: {
			nextEl: "#banner-swiper-next",
			prevEl: "#banner-swiper-prev"
		},
		autoplay: {
			delay: 8e3,
			disableOnInteraction: false,
			pauseOnMouseEnter: true
		},
		speed: 300,
		freeMode: {
			enabled: true,
			sticky: true,
			momentum: true,
			momentumRatio: .5,
			momentumVelocityRatio: .5
		},
		grabCursor: true
	});
	return ss;
}), switchMap((s) => data$.pipe(delay(300), map(() => s), tap((s) => {
	s.updateSlides();
	s.updateProgress();
	s.update();
	s.pagination.update();
}))), shareReplay(1));
const initSwiperSub = swiper$.subscribe();
const destorySwiperSub = swiperDestory$.pipe(switchMap(() => swiper$), tap((s) => s.destroy(true, true))).subscribe();
onMount(() => {
	isReady$.next(true);
	// Attach touch handlers to prevent browser back gesture
	if (swiperContainer) {
		swiperContainer.addEventListener("touchstart", handleTouchStart, { passive: true });
		swiperContainer.addEventListener("touchmove", handleTouchMove, { passive: false });
	}
	return () => {
		swiperDestory$.next(true);
		initSwiperSub.unsubscribe();
		setTimeout(() => destorySwiperSub.unsubscribe(), 300);
		// Remove touch event listeners
		if (swiperContainer) {
			swiperContainer.removeEventListener("touchstart", handleTouchStart);
			swiperContainer.removeEventListener("touchmove", handleTouchMove);
		}
	};
});
let useAlternative = $derived($stateUi.bannerSwiper.bannerSwiperStyle === "alternative");
let useFlatNeutral = $derived($stateUi.bannerSwiper?.useFlatNeutralPagination ?? false);
</script>

<div class="section-container">
	<div
		class="banner-swiper-container"
		class:banner-swiper={!useAlternative}
		class:banner-swiper-alternative={useAlternative}
	>
		<div class="swiper" bind:this={swiperContainer}>
			<div class="swiper-wrapper" class:swiper-wrapper-alternative={useAlternative}>
				{#each $data$ ?? [] as banner, i (banner.id)}
					<div class="swiper-slide" data-swiper-slide-index={i}>
						<Banner width="100%" height="auto" {showRightSideOfImage} {banner} />
					</div>
				{/each}
			</div>
			<div class="swiper-controls-container">
				<div class="swiper-controls">
					<div
						class="sp pagination-override"
						class:sp-alternative={useAlternative}
						class:sp-flat-neutral={useFlatNeutral}
					></div>
				</div>
			</div>
		</div>
	</div>
</div>

<style>
	.section-container {
		position: relative;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		overflow: hidden;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 0px;
		font-family: var(--font-main);
}

	@media (min-width: 768px) {
		.banner-title {
				font-size: 2.25rem;
				line-height: 2.5rem;
				font-weight: 700;
		}
	}

	.banner-swiper-container {
		position: relative;
		height: auto;
		width: 100%;
		overflow: hidden;
		border-radius: 0.5rem;
		overscroll-behavior-x: contain;
}

	.swiper-wrapper {
		/* @apply min-h-[144px] lg:min-h-[230px]; */
	}

	.swiper-wrapper-alternative {
		min-height: 0px;
}

	.swiper-controls {
		position: relative;
		display: flex;
		width: 220px;
		align-items: center;
		justify-content: center;
		margin-bottom: -10px;
		height: 35px;
}

	@media (min-width: 768px) {

		.swiper-controls {
				height: 40px;
		}
}

	.sp {
		margin-top: -8px;
		display: flex;
		justify-content: center;
}

	@media (min-width: 768px) {

		.sp {
				margin-top: 1rem;
		}
		.swiper-controls {
				padding-bottom: 1rem;
		}
}

	.swiper-controls-container {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;
}

	:global(.percentage-home-title) {
		background-image: linear-gradient(
			135deg,
			#ff9e14 0%,
			#ff9e14 0%,
			#ff9e14 20%,
			#fed51b 20%,
			#fed51b 33%,
			#fefe5c 33%,
			#fefe5c 47%,
			#ffffb5 47%,
			#ffffb5 61%,
			#fefe5c 61%,
			#fefe5c 72%,
			#fed51b 72%,
			#fed51b 89%,
			#ff9e14 90%,
			#ff9e14 100%
		);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	:global(.sp .swiper-pagination-bullet) {
		height: 6px;
		width: 6px;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
		opacity: 0.9;
		background-color: var(--color-neutral-300) !important;
}

	:global(.sp .swiper-pagination-bullet-active) {
		height: 6px;
		opacity: 1;
		width: 24px !important;
		border-radius: 25px !important;
		background-color: var(--color-neutral-300);
		position: relative;
		overflow: visible;
}

	:global(.sp .swiper-pagination-bullet-active::before) {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 0;
		background-image: linear-gradient(180deg, var(--primary-brand) 0%, var(--primary-brand) 100%);
		animation: fillProgress 8s linear forwards;
		border-radius: 25px;
		box-shadow: 0px 0px 10px 0px var(--primary-brand);
		z-index: 1;
	}

	:global(.sp.sp-flat-neutral .swiper-pagination-bullet-active::before) {
		box-shadow: none;
	}

	:global(.sp .swiper-pagination-bullet-active::after) {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		border-radius: 25px;
		background-color: transparent;
		z-index: 2;
	}

	/* === FOMO-specific overrides (no animation, simple bullets) === */
	:global(.banner-swiper-alternative) {
		border-radius: 0 !important;
	}

	:global(.sp-alternative .swiper-pagination-bullet) {
		height: 9px !important;
		width: 9px !important;
		--tw-bg-opacity: 1 !important;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1)) !important;
		opacity: 0.2 !important;
}

	:global(.sp-alternative .swiper-pagination-bullet-active) {
		height: 9px !important;
		width: 9px !important;
		--tw-bg-opacity: 1 !important;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1)) !important;
		opacity: 0.9 !important;
}

	:global(.sp-alternative .swiper-pagination-bullet-active::before),
	:global(.sp-alternative .swiper-pagination-bullet-active::after) {
		content: none !important;
		display: none !important;
	}

	@keyframes fillProgress {
		0% {
			width: 0;
		}
		100% {
			width: 100%;
		}
	}
</style>
